import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import CrisisResourceBanner from '../../components/CrisisResourceBanner';
import HeroSection from './components/HeroSection';
import TrustSignals from './components/TrustSignals';
import SupportOptions from './components/SupportOptions';
import HowItWorks from './components/HowItWorks';
import CrisisResources from './components/CrisisResources';
import PrivacyNotice from './components/PrivacyNotice';
import Footer from './components/Footer';
import { adminService } from '../../services/adminService';

const OrganizationLanding = () => {
  const navigate = useNavigate();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [organizationData, setOrganizationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchOrganizationData();
  }, []);

  const fetchOrganizationData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { data, error: fetchError } = await adminService?.getOrganization();
      
      if (fetchError) {
        console.error('Error fetching organization:', fetchError);
        setError('Unable to load organization data');
        // Set fallback data
        setOrganizationData({
          name: "Cwtch",
          logo: "/assets/images/CWTCH-1764836850908.png",
          logoAlt: "Cwtch Mental Health Support Platform"
        });
      } else if (data) {
        setOrganizationData({
          name: data?.name || "Cwtch",
          logo: data?.branding?.logo || "/assets/images/CWTCH-1764836850908.png",
          logoAlt: `${data?.name || 'Cwtch'} company logo`
        });
      } else {
        // No organization found, use default
        setOrganizationData({
          name: "Cwtch",
          logo: "/assets/images/CWTCH-1764836850908.png",
          logoAlt: "Cwtch Mental Health Support Platform"
        });
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Unable to load organization data');
      // Set fallback data
      setOrganizationData({
        name: "Cwtch",
        logo: "/assets/images/CWTCH-1764836850908.png",
        logoAlt: "Cwtch Mental Health Support Platform"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSSOLogin = () => {
    setIsAuthenticating(true);

    setTimeout(() => {
      const mockUser = {
        id: "user_12345",
        email: "sarah.johnson@techcorp.com",
        name: "Sarah Johnson",
        organization: "TechCorp Global",
        role: "seeker",
        authenticated: true,
        authProvider: "Azure AD"
      };

      localStorage.setItem('mindconnect_user', JSON.stringify(mockUser));
      localStorage.setItem('mindconnect_auth_token', 'mock_jwt_token_' + Date.now());

      setIsAuthenticating(false);
      navigate('/seeker-intake-flow');
    }, 2000);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Cwtch - {organizationData?.name} Mental Health Support</title>
        <meta
          name="description"
          content="Access confidential mental health support through lived-experience volunteers and licensed professionals. Connect in minutes with culturally-matched helpers." />

        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <CrisisResourceBanner />

        <main className="pt-16">
          <HeroSection
            organizationName={organizationData?.name}
            organizationLogo={organizationData?.logo}
            onSSOLogin={handleSSOLogin} />


          <TrustSignals />

          <SupportOptions />

          <HowItWorks />

          <CrisisResources />

          <PrivacyNotice />
        </main>

        <Footer />

        {isAuthenticating &&
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1000] flex items-center justify-center">
            <div className="bg-card rounded-xl border border-border shadow-xl p-8 max-w-md w-full mx-4">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  Authenticating with SSO
                </h3>
                <p className="text-sm text-muted-foreground">
                  Securely connecting to {organizationData?.name}'s identity provider...
                </p>
              </div>
            </div>
          </div>
        }
      </div>
    </>);

};

export default OrganizationLanding;